import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { LOGO_WHITE } from "@/lib/assets";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-reset", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] relative overflow-hidden orio-grain">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(255,176,32,0.18) 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(52,211,153,0.18) 0%, transparent 60%)" }} />
      </div>

      <Link to="/" data-testid="auth-back" className="absolute top-6 left-6 z-10 flex items-center -my-1">
        <div aria-label="ORIO" className="h-7 w-[72px]"
          style={{
            background: "linear-gradient(120deg, #ff3b30 0%, #ffb020 50%, #34d399 100%)",
            backgroundSize: "200% 200%",
            animation: "orio-bg-shift 6s ease infinite",
            WebkitMask: `url(${LOGO_WHITE}) no-repeat center / contain`,
            mask: `url(${LOGO_WHITE}) no-repeat center / contain`,
          }} />
      </Link>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md glass rounded-3xl p-8 sm:p-10 border border-[#F0EBE3]" data-testid="forgot-page">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-[#9A9A9A] hover:text-[#1A1A1A] mb-4">
            <ArrowLeft className="h-3 w-3" /> Torna al login
          </Link>

          {!sent ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-3">Recupero accesso</div>
              <h1 className="font-display text-3xl tracking-tight">Password dimenticata?</h1>
              <p className="text-[#6B6B6B] mt-2 text-sm leading-relaxed">
                Inserisci la tua email. Ti invieremo un link per reimpostare la password (valido 1 ora).
              </p>
              <form onSubmit={onSubmit} className="mt-7 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#9A9A9A]">Email</label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A]" />
                    <input
                      data-testid="forgot-email"
                      type="email" required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[#F0EBE3] focus:border-white/30 outline-none"
                      placeholder="tu@esempio.it"
                    />
                  </div>
                </div>
                <button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={loading}
                  className="orio-cta-btn relative w-full px-6 py-3.5 rounded-full font-semibold text-black overflow-hidden disabled:opacity-50"
                >
                  <span className="relative z-10 inline-flex items-center justify-center gap-2">
                    <Send className="h-4 w-4" /> {loading ? "Invio…" : "Invia link di recupero"}
                  </span>
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent">
              <div className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center"
                style={{ background: "rgba(52,211,153,0.15)", color: "#34D399" }}>
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="font-display text-2xl tracking-tight mt-5 text-center">Controlla la tua email</h1>
              <p className="text-[#6B6B6B] mt-3 text-sm leading-relaxed text-center">
                Se l'indirizzo <span className="text-[#1A1A1A]">{email}</span> è registrato su ORIO,
                ti abbiamo inviato un link per reimpostare la password. Il link scade tra 1 ora.
              </p>
              <p className="text-[#9A9A9A] text-xs mt-3 text-center">
                Non hai ricevuto nulla? Controlla nello spam.
              </p>
              <Link to="/login" data-testid="forgot-back-login"
                className="mt-7 block w-full text-center px-5 py-3 rounded-full bg-white text-black font-semibold">
                Torna al login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
